/**
 * Header bar for the Warehouse Layout Editor.
 * Contains title, view mode toggle, undo/redo and action buttons.
 * Lagringsstatus vises i statuslinjen nederst (SelectionStatusBar).
 */

import React, { useRef } from 'react';
import {
  ArrowLeft,
  Save,
  Download,
  Upload,
  Settings,
  Undo2,
  Redo2,
  PersonStanding,
} from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { ViewMode } from '../types/warehouseLayout';
import { exportLayoutAsJson, importLayoutFromJson } from '../utils/layoutStorage';

interface EditorHeaderProps {
  onBack?: () => void;
}

const EditorHeader: React.FC<EditorHeaderProps> = ({ onBack }) => {
  const { state, dispatch, save, canUndo, canRedo } = useLayoutEditor();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { layout, editorState } = state;
  if (!layout) return null;

  const handleViewModeChange = (mode: ViewMode) => {
    dispatch({ type: 'SET_VIEW_MODE', mode });
  };

  const handleExport = () => {
    exportLayoutAsJson(layout);
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const imported = await importLayoutFromJson(file);
      imported.warehouseId = layout.warehouseId;
      dispatch({ type: 'IMPORT_LAYOUT', layout: imported });
    } catch (err) {
      dispatch({ type: 'SET_ERROR', error: err instanceof Error ? err.message : t('manager.modules.warehouseLayout.editor.importFailed', {}, 'Import feilet') });
    }
    e.target.value = '';
  };

  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={t('common.buttons.back', {}, 'Tilbake')}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-xl font-bold text-gray-900">{t('manager.modules.warehouseLayout.editor.title', {}, '3D Lagervisning')}</h1>
            <p className="text-sm text-gray-500">
              {layout.name} &bull; {t('manager.modules.warehouseLayout.editor.racksPlaced', { '0': layout.racks.length }, `${layout.racks.length} reoler plassert`)}
              {editorState.selectedRacks.size > 0 && (
                <span className="text-blue-600 ml-2">
                  ({t('manager.modules.warehouseLayout.editor.selectedCount', { '0': editorState.selectedRacks.size }, `${editorState.selectedRacks.size} valgt`)})
                </span>
              )}
            </p>
          </div>
        </div>

        {/* View mode toggle */}
        <div className="flex items-center bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => handleViewModeChange('2d')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              editorState.viewMode === '2d'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('manager.modules.warehouseLayout.editor.view2d', {}, '2D Editor')}
          </button>
          <button
            onClick={() => handleViewModeChange('3d')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              editorState.viewMode === '3d'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('manager.modules.warehouseLayout.editor.view3d', {}, '3D Visning')}
          </button>
          <button
            onClick={() => handleViewModeChange('first-person')}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
              editorState.viewMode === 'first-person'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            title={t('manager.modules.warehouseLayout.editor.firstPersonTooltip', {}, 'Gå rundt i lageret (førsteperson)')}
          >
            <PersonStanding className="w-4 h-4" />
            {t('manager.modules.warehouseLayout.editor.firstPerson', {}, 'Førsteperson')}
          </button>
        </div>

        {/* Actions — lagringsstatus vises i statuslinjen nederst */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => dispatch({ type: 'UNDO' })}
            disabled={!canUndo}
            className={`p-2 rounded-lg ${canUndo ? 'hover:bg-gray-100' : 'opacity-30 cursor-not-allowed'}`}
            title={t('manager.modules.warehouseLayout.editor.undoTooltip', {}, 'Angre (Ctrl+Z)')}
          >
            <Undo2 className="w-5 h-5" />
          </button>
          <button
            onClick={() => dispatch({ type: 'REDO' })}
            disabled={!canRedo}
            className={`p-2 rounded-lg ${canRedo ? 'hover:bg-gray-100' : 'opacity-30 cursor-not-allowed'}`}
            title={t('manager.modules.warehouseLayout.editor.redoTooltip', {}, 'Gjenta (Ctrl+Shift+Z)')}
          >
            <Redo2 className="w-5 h-5" />
          </button>

          <div className="w-px h-6 bg-gray-200 mx-1" />

          <button onClick={save} className="p-2 hover:bg-gray-100 rounded-lg" title={t('common.buttons.save', {}, 'Lagre')}>
            <Save className="w-5 h-5" />
          </button>
          <button onClick={handleExport} className="p-2 hover:bg-gray-100 rounded-lg" title={t('manager.modules.warehouseLayout.editor.exportJson', {}, 'Eksporter JSON')}>
            <Download className="w-5 h-5" />
          </button>
          <label className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer" title={t('manager.modules.warehouseLayout.editor.importJson', {}, 'Importer JSON')}>
            <Upload className="w-5 h-5" />
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
            />
          </label>
          <button
            onClick={() => dispatch({ type: 'SET_SIDEBAR_TAB', tab: 'settings' })}
            className={`p-2 rounded-lg ${state.sidebarTab === 'settings' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
            title={t('manager.modules.warehouseLayout.editor.settingsTooltip', {}, 'Innstillinger')}
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditorHeader;
